# AN EXPIRED SESSION IS A SIGN-IN SCREEN, NEVER A CRASH SCREEN — card
# 6ab16e9c013e5b1a5ad54628.
#
# PRODUCTION, 2026-09-21 11:46 MT, CrashLog fingerprint bbdc60d. A parent on
# Windows Chrome opened /app#/dashboard with a stored token the server no
# longer honours. Boot tried it twice and the console tail records both:
#
#   1. Login.whoami   -> 401. confirmSessionIdentity is deliberately never
#                        fatal, so it warned and carried on.
#   2. Account.getAccount -> 401, thrown out of the boot read, past
#                        retryAbortedRead (a 401 is not transient, correctly),
#                        into render_react_error and the ErrorBoundary.
#
# So the family's first screen was "We have encountered an error", and the
# only way out of it was the crash screen's Clear & Reload — a button that
# asks a parent to understand localStorage. What should have happened is the
# thing every site does with an expired session: show the sign-in screen.
#
# WHAT THIS MODULE IS. One predicate and one action, so that both places that
# meet the dead token agree about it. `confirmSessionIdentity` meets it first
# and can save the second doomed request; the boot read meets it for a
# principal that has no `whoami` on its manifest at all (a child's Student
# session), and has to handle it too. One definition, two callers — the same
# arrangement `isRetryable` has in transition_recovery.coffee, and for the
# same reason: a token given up on in one place and retried in the other is
# worse than either answer.
#
# WHY 401 AND NOTHING ELSE. These two reads are made with a stored credential
# before the app has drawn anything, and 401 is the server saying the
# credential did not authenticate. A 403 is a principal that IS authenticated
# and may not do this, which is a product question and belongs on the error
# path it already has. A 500 is ours. Neither is a reason to sign somebody out.
#
# WHY NOT forgetIdentity(). crash_reporter's lastKnown identity is what lets
# support name the family behind a login-page crash, and it is cleared on an
# EXPLICIT logout only (crash_reporter.coffee says so in its own words). A
# token that timed out is not the family leaving the device.

# The framework's client API rejects with an axios-shaped error; a bare fetch
# wrapper puts the number somewhere else. Read all three rather than assuming
# which layer produced it.
export statusOf = (err) ->
  err?.response?.status ? err?.status ? err?.statusCode ? null

export isDeadSession = (err) ->
  return false unless err?
  Number(statusOf err) is 401

# Drop the stored session and land on the sign-in screen.
#
# The hash is emptied because the route that was asked for is the route that
# just failed: `#/dashboard` with no session sends ui-router through
# requireLogin to `login` anyway, and arriving there via a redirect from a
# screen that cannot render is a flicker the family does not need to watch.
#
# `showSignIn` is the shell's own render call (start_react). Passed in rather
# than imported, because each shell has its own and this module is shared.
export signOutToSignIn = (showSignIn, deps = {}) ->
  win = deps.window ? (if typeof window != 'undefined' then window else null)
  api = deps.api ? win?.app?.loopbackAPI
  try
    api?.clearUser?()
  catch e
    null
  try
    win.location.hash = '' if win?.location?
  catch e
    null
  showSignIn?()
  true

# A SESSION WHOSE MANIFEST DOES NOT SERVE THE BOOT READ — card
# 6ab69fbe027287c485001e30.
#
# PRODUCTION, 2026-09-25 10:12 MT, CrashLog fingerprint 4d62018a, client
# teacher3.32-c205c71b988a, route /teacher#/tokens. Three milliseconds after
# "Calling ready":
#
#   TypeError: this.loopbackAPI.Account.getAccount is not a function
#
# The shell found a token under `$teacher$`, so `isLoggedIn()` answered true
# and boot went straight into `Application.refresh()`. But `/clientapi` had
# answered 200 with the ANONYMOUS manifest — it does that whenever a presented
# token authorizes to no usable role, not only when no token is presented — and
# on that manifest `Account` carries `getPlans` and nothing else. The client
# believed it had a session; the manifest it was holding disagreed, and nothing
# reconciled the two. The family got the crash screen.
#
# THIS IS NOT THE 401 CASE ABOVE, and that is why it needs its own predicate.
# A dead token is a request that comes back refused; this one never gets to
# make a request at all. Calling an absent method throws SYNCHRONOUSLY, before
# any promise exists to reject, so every `.catch` downstream is already too
# late — the same reading as the games screen's audio warm-up (card
# 6aaedd51a822f303b055f82e): ask the manifest what it will serve.
#
# WHY `Account.getAccount` BY NAME rather than something more general. It is
# what `Application.refresh` calls, and `refresh` is the boot read of every
# shell. A shell whose boot read changes should change this with it, loudly,
# rather than inherit a guard that no longer guards the call being made.
export servesBootRead = (api) ->
  typeof api?.Account?.getAccount == 'function'

# A SESSION WITH NO USER ID IS NOT A SESSION — card 6ac8347717fd547aec27eb48.
#
# PRODUCTION, 2026-10-07 and 2026-10-08, CrashLog 5c9a9e82, 2f157232 and
# 340b1790: nine rows from two devices, both chat-app in-app browsers (WeChat,
# DaumApps), on /app and /teacher#login. A sign-in link arrived with its
# `access_token` and without its `uid` — pasted into a chat and trimmed on the
# way; nothing we mint omits it. The shells install whatever the link carries,
# and ClientAPI.setUser turns an absent uid into currentUserId ''. A token is
# in storage, so isLoggedIn() is true and boot makes its read:
# Account.getAccount({id: ''}) is POST /Accounts//getAccount, a 404. The crash
# screen reloads, `_load` hands back null for the '' it saved, and the next
# read is {id: null}, "id is malformed for getAccount", a 400 — on every
# reload, because the token in storage is the cause.
#
# The token itself was good. So the shells first ASK whose it is
# (confirmSessionIdentity, below), and a token that can say signs the visitor
# in as the link meant to. What is still unidentified after that is not read
# with: the boot sends it to sign-in, and Application.refresh refuses it too,
# so no other way into the read can reach the 404 or the 400.
#
# 'null' and 'undefined' are counted as absent because they are what an id
# looks like after it has been through a template string or a query string —
# `uid=undefined` is a link somebody built from nothing.
export hasBootIdentity = (api) ->
  id = api?.currentUserId
  return false unless id?
  text = "#{id}".trim()
  text.length > 0 and text not in ['null', 'undefined']

# THE SESSION'S IDENTITY COMES FROM THE TOKEN, NOT THE ADDRESS BAR —
# card 6aaeb53dc0a495ac26c96c2c.
#
# A server-minted sign-in link is /app?access_token=..&uid=.., and every
# handoff stored that uid as currentUserId. It is a URL parameter: a visitor
# can put anybody's id there. Taking the invitee's OWN delivered link and
# swapping its uid for the inviting parent's left the browser holding a
# session labelled as the parent (sharing-invitations case 6, red on elfeet
# QA 2026-09-19).
#
# WHAT IT IS AND IS NOT. Measured on ripperws3 2026-09-19: the SERVER
# authorises by account membership derived from the token and ignores the uid
# entirely — a token from another account gets 404 for the parent's login, so
# there is no cross-account reach. What is wrong is the CLIENT's idea of who
# it is, and currentUserId is what Application.refresh() asks
# Account.getAccount about. So ask the server whose token this is and believe
# that instead.
#
# ASKED WHENEVER THERE IS A TOKEN, not only when there is a uid to check —
# card 6ac8347717fd547aec27eb48. A link that lost its uid on the way is the
# case where the server's answer matters most: there is nothing else to boot
# on. Both the parent and the teacher shell ask, from initialize(), before
# the first authenticated read.
#
# NEVER FATAL. Login.whoami is on the manifest for every signed-in Login role
# but not for a child's Student principal, and a link can be opened
# offline-ish or against an older server. Any failure leaves the session
# exactly as it was — hasBootIdentity, above, decides what an unidentified one
# is worth.
export confirmSessionIdentity = (lbapi) ->
  return unless lbapi?.accessTokenId and lbapi.Login?.whoami?
  try
    answer = await lbapi.Login.whoami({})
    served = answer?.data?.id
    type = answer?.data?.type
    return unless served? and "#{type}" == 'Login'
    return if "#{served}" == "#{lbapi.currentUserId}"
    if hasBootIdentity lbapi
      console.warn "This link's uid (#{lbapi.currentUserId}) is not whose token it carries (#{served}) — using the server's answer"
    else
      console.warn "This session carries no user id — its token belongs to #{served}, using that"
    lbapi.setUser lbapi.accessTokenId, "#{served}", undefined, lbapi.currentStudentId
    lbapi.resetAuthHeader()
  catch err
    # A 401 HERE IS NOT A FAILURE TO CONFIRM, IT IS THE ANSWER — card
    # 6ab16e9c013e5b1a5ad54628. Every other failure leaves the session
    # alone, which is what "never fatal" was written for: an older server,
    # a principal with no whoami on its manifest, a dropped request. A 401
    # is none of those. It is the server saying this stored token does not
    # authenticate, and the only thing left to do with it is stop using it
    # — before getAccount asks the same question and gets the same answer
    # into the error boundary.
    if isDeadSession err
      console.warn "The stored session token is no longer valid (401) — signing out to the sign-in screen"
      return 'dead'
    console.warn "Could not confirm whose token this session carries: #{err?.message or err}"
  return
